import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/data/site";

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-9 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-[74px] flex items-center justify-between gap-4">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2 shrink-0">
            <span
              className="h-10 w-10 shrink-0 mix-blend-multiply"
              style={{
                backgroundImage: `url(${BRAND.logo})`,
                backgroundSize: "225%",
                backgroundPosition: "50% 20%",
                backgroundRepeat: "no-repeat",
              }}
              aria-hidden="true"
            />
            <span className="text-lg md:text-xl font-bold tracking-[0.12em] text-ink whitespace-nowrap">
              FEMORA LIFE
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-gray-600 hover:text-ink transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-5 shrink-0">
            <Link
              to="/contact"
              data-testid="header-contact-link"
              className="text-sm font-medium text-gray-600 hover:text-ink transition-colors duration-200"
            >
              Contact
            </Link>
            <Link
              to="/contact"
              data-testid="header-contact-cta"
              className="rounded-full bg-ink text-white text-sm font-semibold px-6 py-2.5 hover:bg-rose transition-colors duration-200"
            >
              Enquire
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-ink"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 top-[110px] z-40 md:hidden bg-white border-b border-gray-200"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  className="py-3 text-base font-medium text-ink border-b border-gray-100"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/contact"
                className="mt-4 rounded-full bg-ink text-white text-sm font-semibold px-6 py-3 text-center"
                data-testid="mobile-nav-contact"
              >
                Contact / Enquire
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
